/**
 * @voltxampere/engine — Residual stall detector (Sprint 8c)
 *
 * The Newton-Raphson loop in {@link ./newton-raphson.ts} needs to
 * decide when to activate Armijo backtracking (expensive: one
 * residual evaluation per backtrack, equivalent to a Newton iter).
 * Two triggers are acceptable:
 *
 *   1. Initial conditions are supplied — the caller already knows
 *      roughly where the OP is, so every Newton step is worth
 *      scrutinising against the reduced-KCL residual.
 *
 *   2. Newton has stalled — the residual stopped falling
 *      substantially, suggesting Newton is orbiting or diverging.
 *      Armijo's backtracking finds a shorter step that makes
 *      progress.
 *
 * This module implements (2). The detector keeps a short rolling
 * window of recent residual norms (default 3 entries) and fires
 * when the oldest→newest reduction is below a threshold (default
 * 5%). The window size is small because PNP CE's limit cycle has
 * period ~3-6 iter; a longer window would over-smooth the signal.
 * The threshold is 5% because linear-circuit Newton converges in
 * one step (first-iter residual is already negligible, so the
 * 3-iter window never fills), whereas a genuinely stalled
 * pathological circuit reduces by <1% per iter.
 *
 * The detector is stateless across solver calls: it is reset at
 * the start of every Newton drive and at every fallback transition
 * (gmin → source → Anderson). Each continuation gets a fresh
 * detection window so stall decisions don't leak across regimes
 * where the physics has legitimately changed.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError } from "@voltxampere/shared";
export const DEFAULT_STALL_OPTIONS = {
    windowSize: 3,
    threshold: 0.05,
};
/**
 * Rolling-window residual stall detector.
 *
 * Usage:
 *
 * ```ts
 * const detector = new StallDetector();
 * for (let iter = 1; iter <= max; iter++) {
 *   const res = computeReducedResidual(...);
 *   detector.record(res);
 *   if (detector.isStalled()) activateArmijo();
 * }
 * ```
 */
export class StallDetector {
    options;
    history = [];
    constructor(options = DEFAULT_STALL_OPTIONS) {
        if (!Number.isInteger(options.windowSize) || options.windowSize < 2) {
            throw new ContractViolationError(`StallDetector: windowSize must be an integer ≥ 2, got ${String(options.windowSize)}`);
        }
        if (!(options.threshold > 0 && options.threshold < 1)) {
            throw new ContractViolationError(`StallDetector: threshold must lie in (0, 1), got ${String(options.threshold)}`);
        }
        this.options = options;
    }
    /**
     * Record a residual norm sample. The window is FIFO of size
     * `windowSize + 1` so `isStalled()` can compare oldest vs.
     * newest across exactly `windowSize` iterations.
     */
    record(residualNorm) {
        if (!(residualNorm >= 0)) {
            throw new ContractViolationError(`StallDetector.record: residualNorm must be non-negative, got ${String(residualNorm)}`);
        }
        this.history.push(residualNorm);
        const max = this.options.windowSize + 1;
        while (this.history.length > max)
            this.history.shift();
    }
    /**
     * True iff `windowSize + 1` samples have been recorded AND the
     * residual is "not changing significantly" over the window (in
     * either direction).
     *
     * "Not changing significantly" means `|reduction| < threshold`
     * where `reduction = (oldest − newest) / oldest`:
     *
     *   - Large positive reduction (`reduction > threshold`): the
     *     residual is SHRINKING fast — Newton is converging well,
     *     leave it alone.
     *   - Small positive reduction (`0 < reduction < threshold`):
     *     shrinking slowly — classic stall.
     *   - Near-zero (`|reduction| ≈ 0`): flat — stall.
     *   - Small negative reduction (`0 > reduction > −threshold`):
     *     residual is oscillating in place with small growth — also
     *     stall (Armijo's backtracking is the right rescue tool for
     *     this oscillation).
     *   - Large negative reduction (`reduction < −threshold`): the
     *     residual is actively GROWING fast — Newton is diverging,
     *     NOT a stall. Armijo can't fix divergent iterates, so we
     *     fall through here and let the Newton driver hit maxIter,
     *     at which point the DC solver's gmin / source stepping
     *     ladder takes over.
     *
     * Sprint 8c.1 note: this function previously used the raw-sign
     * test `reduction < threshold`, which caught divergence as
     * "stall" because a negative number is less than a positive
     * threshold. The behaviour was defensible but semantically
     * confusing (the comment said "reduction below threshold" with
     * no mention of divergence). The `Math.abs` form makes the
     * "flat window in either direction" intent explicit and keeps
     * divergence out of Armijo's domain where it does not belong.
     *
     * Edge case: if `oldest === 0`, no further reduction is possible
     * and the iterate is trivially converged — we return `false` (not
     * stalled) because the Newton driver's convergence check will
     * catch this first.
     */
    isStalled() {
        if (this.history.length < this.options.windowSize + 1)
            return false;
        const oldest = this.history[0] ?? 0;
        const newest = this.history[this.history.length - 1] ?? 0;
        if (oldest <= 0)
            return false;
        const reduction = (oldest - newest) / oldest;
        return Math.abs(reduction) < this.options.threshold;
    }
    /** Clear all recorded samples. Invoked at fallback transitions. */
    reset() {
        this.history.length = 0;
    }
    /** Internal-facing: current window contents (test instrumentation). */
    snapshot() {
        return [...this.history];
    }
}
//# sourceMappingURL=stall-detection.js.map